import React from 'react';
import { motion } from 'framer-motion';
import { 
  Settings as SettingsIcon, 
  Volume2, 
  VolumeX, 
  Play, 
  Pause, 
  Sun, 
  Moon, 
  Monitor,
  Save,
  RotateCcw,
  Download,
  Upload
} from 'lucide-react';
import { useGameStore } from '../store/gameStore';

const Settings: React.FC = () => {
  const { 
    settings, 
    updateSettings, 
    resetGame, 
    currentAge, 
    stats, 
    completedDecisions 
  } = useGameStore();

  const handleSettingChange = (key: string, value: any) => {
    updateSettings({ [key]: value });
  };

  const handleExportGame = () => {
    const gameData = {
      currentAge,
      stats,
      completedDecisions,
      settings,
      timestamp: new Date().toISOString(),
    };
    
    const dataStr = JSON.stringify(gameData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `sppefrun-life-save-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  };

  const handleImportGame = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const gameData = JSON.parse(e.target?.result as string);
        // This would typically be handled by the store
        console.log('Import game data:', gameData);
        alert('Game imported successfully!');
      } catch (error) {
        alert('Invalid game file. Please select a valid SppefRun Life save file.');
      }
    };
    reader.readAsText(file);
  };

  const settingSections = [
    {
      title: 'Game Settings',
      icon: SettingsIcon,
      settings: [
        {
          key: 'difficulty',
          label: 'Difficulty Level',
          type: 'select',
          options: [
            { value: 'easy', label: 'Easy' },
            { value: 'normal', label: 'Normal' },
            { value: 'hard', label: 'Hard' },
          ],
        },
        {
          key: 'autoSave',
          label: 'Auto Save',
          type: 'toggle',
          description: 'Automatically save your progress',
        },
      ],
    },
    {
      title: 'Audio & Visual',
      icon: Volume2,
      settings: [
        {
          key: 'soundEnabled',
          label: 'Sound Effects',
          type: 'toggle',
          description: 'Enable sound effects and notifications',
        },
        {
          key: 'animationsEnabled',
          label: 'Animations',
          type: 'toggle',
          description: 'Enable smooth animations and transitions',
        },
        {
          key: 'theme',
          label: 'Theme',
          type: 'select',
          options: [
            { value: 'light', label: 'Light', icon: Sun },
            { value: 'dark', label: 'Dark', icon: Moon },
            { value: 'auto', label: 'Auto', icon: Monitor },
          ],
        },
      ],
    },
  ];

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-4xl font-bold text-slate-800 mb-4">Settings</h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
          Customize your SppefRun Life experience and manage your game data
        </p>
      </motion.div>

      {/* Settings Sections */}
      <div className="space-y-8">
        {settingSections.map((section, sectionIndex) => {
          const Icon = section.icon;
          return (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-soft border border-white/20"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                  <Icon size={20} className="text-primary-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">{section.title}</h2>
              </div>

              <div className="space-y-6">
                {section.settings.map((setting) => (
                  <div key={setting.key} className="flex items-center justify-between py-4 border-b border-slate-200 last:border-b-0">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-800 mb-1">
                        {setting.label}
                      </h3>
                      {setting.description && (
                        <p className="text-sm text-slate-600">{setting.description}</p>
                      )}
                    </div>

                    <div className="ml-4">
                      {setting.type === 'toggle' ? (
                        <button
                          onClick={() => handleSettingChange(setting.key, !settings[setting.key as keyof typeof settings])}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings[setting.key as keyof typeof settings]
                              ? 'bg-primary-500'
                              : 'bg-slate-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings[setting.key as keyof typeof settings]
                                ? 'translate-x-6'
                                : 'translate-x-1'
                            }`}
                          />
                        </button>
                      ) : setting.type === 'select' ? (
                        <select
                          value={settings[setting.key as keyof typeof settings] as string}
                          onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                          className="px-4 py-2 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          {setting.options?.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Game Data Management */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-soft border border-white/20"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-warning-100 rounded-xl flex items-center justify-center">
            <Save size={20} className="text-warning-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Game Data</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Export Game */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Export Game</h3>
            <p className="text-slate-600 text-sm">
              Download your current game progress to save it locally or share with others.
            </p>
            <button
              onClick={handleExportGame}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
            >
              <Download size={18} />
              <span>Export Save File</span>
            </button>
          </div>

          {/* Import Game */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Import Game</h3>
            <p className="text-slate-600 text-sm">
              Load a previously saved game file to continue your journey.
            </p>
            <label className="flex items-center space-x-2 px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-xl transition-colors cursor-pointer">
              <Upload size={18} />
              <span>Import Save File</span>
              <input
                type="file"
                accept=".json"
                onChange={handleImportGame}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </motion.div>

      {/* Danger Zone */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-red-50 border-2 border-red-200 rounded-2xl p-6"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
            <RotateCcw size={20} className="text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-red-800">Danger Zone</h2>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Reset Game</h3>
            <p className="text-red-600 text-sm mb-4">
              This will permanently delete all your progress and start a new game. This action cannot be undone.
            </p>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to reset your game? This action cannot be undone.')) {
                  resetGame();
                }
              }}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors"
            >
              Reset Game
            </button>
          </div>
        </div>
      </motion.div>

      {/* Game Statistics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-6 text-white"
      >
        <h2 className="text-2xl font-bold mb-4">Current Game Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold">{currentAge}</div>
            <div className="text-primary-100">Current Age</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{completedDecisions.length}</div>
            <div className="text-primary-100">Decisions Made</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">
              {Math.round(Object.values(stats).reduce((a, b) => a + b, 0) / Object.keys(stats).length)}
            </div>
            <div className="text-primary-100">Average Score</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">
              {Object.values(stats).filter(v => v >= 80).length}
            </div>
            <div className="text-primary-100">Excellent Areas</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Settings;
